/**
 * useRemainingHeight.jsx
 *
 * Custom React hook and helper functions to calculate the remaining height
 * inside a container element, accounting for optional header elements,
 * padding, and margin offsets. The calculations are optimized for smooth
 * resizing using debouncing and requestAnimationFrame to prevent layout jitter.
 * If no observedRef is provided, window.innerHeight is used as the max height.
 */

import { useCallback, useEffect, useRef, useState } from "react";

/** 
 * Pixel offset to account for container padding/margin.
 * @private
 */
const MAGIC_OFFSET = 2 * 24 + 3 * 8;

/** 
 * Debounce delay (ms) for resize event handling.
 * @private
 */
const DEBOUNCE_TIMEOUT = 30;

/**
 * Supported rounding modes.
 * @public
 */
const ROUNDING_MODES = {
  /** Round down to nearest integer */
  FLOOR: "floor",
  /** Round up to nearest integer */
  CEIL: "ceil",
  /** Round to nearest integer */
  ROUND: "round",
};

/**
 * Rounds a number according to the specified mode.
 *
 * @param {number} value - The number to round.
 * @param {keyof typeof ROUNDING_MODES} [mode=ROUNDING_MODES.FLOOR] - Rounding mode to use.
 * @returns {number} The rounded result.
 * @private
 */
const applyRounding = (value, mode = ROUNDING_MODES.FLOOR) => {
  switch (mode) {
    case ROUNDING_MODES.CEIL:
      return Math.ceil(value);
    case ROUNDING_MODES.ROUND:
      return Math.round(value);
    case ROUNDING_MODES.FLOOR:
    default:
      return Math.floor(value);
  }
};

/**
 * Calculates the remaining height inside a container element by subtracting
 * the heights of optional header elements and applying an optional offset.
 *
 * @param {Object} params - Parameters object
 * @param {HTMLElement} params.observedEl - The container element to measure.
 * @param {React.RefObject<HTMLElement>[]} [params.headerRefs=[]] - Optional array of refs to header elements inside the container.
 * @param {number} [params.magicOffset=MAGIC_OFFSET] - Pixel offset to account for padding/margin.
 * @param {keyof typeof ROUNDING_MODES} [params.roundingMode=ROUNDING_MODES.FLOOR] - Rounding mode to apply to the calculated height.
 * @returns {number} Remaining height in pixels (clamped to zero if negative).
 * @public
 */
const calculateRemainingHeight = ({
  observedEl,
  headerRefs = [],
  magicOffset = MAGIC_OFFSET,
  roundingMode = ROUNDING_MODES.FLOOR,
}) => {
  if (!observedEl) return 0;

  const totalHeaderHeight = headerRefs.reduce(
    (sum, ref) => sum + (ref.current?.offsetHeight || 0),
    0
  );

  return applyRounding(
    observedEl.offsetHeight - totalHeaderHeight - magicOffset,
    roundingMode
  );
};

/**
 * React hook to calculate the remaining height inside a container element.
 *
 * The hook handles layout resizing by:
 * - Accounting for optional header elements, padding, and margin offsets.
 * - Debouncing updates to improve performance.
 * - Using requestAnimationFrame to avoid layout jumpiness during resizing.
 *
 * If no `observedRef` is provided, the maximum height is set to `window.innerHeight`.
 *
 * Subpixel jitter is filtered, ensuring updates occur only when the height has changed significantly.
 *
 * @param {Object} params - Hook parameters.
 * @param {React.RefObject<HTMLElement>} params.observedRef - Ref pointing to the container element to measure.
 * @param {React.RefObject<HTMLElement>[]} [params.headerRefs=[]] - Array of refs to header elements inside the container.
 * @param {boolean} [params.isActive=true] - Whether the hook is active and should perform calculations.
 * @param {boolean} [params.disableUpdate=false] - Whether to disable updates to the height calculation.
 * @param {number} [params.minReportedHeight=0] - Minimum height to report, ensuring the component doesn't shrink below this threshold.
 * @param {number} [params.magicOffset=MAGIC_OFFSET] - Pixel offset to account for padding/margin.
 * @param {keyof typeof ROUNDING_MODES} [params.roundingMode=ROUNDING_MODES.FLOOR] - Rounding mode applied to the calculated height.
 * @returns {number} The remaining height in pixels, clamped to `minReportedHeight` if necessary.
 * @public
 */
const useRemainingHeight = ({
  observedRef,
  headerRefs = [],
  isActive = true,
  disableUpdate = false,
  minReportedHeight = 0,
  magicOffset = MAGIC_OFFSET,
  roundingMode = ROUNDING_MODES.FLOOR,
}) => {
  const [remainingHeight, setRemainingHeight] = useState(0);
  const frameRef = useRef(null);
  const timeoutRef = useRef(null);
  const lastRemainingHeight = useRef(null);

  /**
   * Function to update the remaining height inside the container element.
   */
  const updateHeight = useCallback(() => {
    if (!isActive || disableUpdate || !observedRef.current) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      const newHeight = calculateRemainingHeight({
        observedEl: observedRef.current,
        headerRefs,
        magicOffset,
        roundingMode,
      });

      if (frameRef.current) cancelAnimationFrame(frameRef.current);

      // Filter out subpixel jitter: Only update if height has changed significantly
      if (lastRemainingHeight.current !== null && Math.abs(newHeight - lastRemainingHeight.current) <= 1) return;

      // Schedule setState in the next animation frame
      frameRef.current = requestAnimationFrame(() => {
        lastRemainingHeight.current = newHeight;
        setRemainingHeight(Math.max(
          Math.min(newHeight, observedRef.current?.offsetHeight || window.innerHeight),
          minReportedHeight
        ));
      });
    }, DEBOUNCE_TIMEOUT);
  }, [isActive, disableUpdate, observedRef, headerRefs, magicOffset, roundingMode, minReportedHeight]);

  useEffect(() => {
    if (!isActive || disableUpdate || !observedRef.current) return;

    const containerObserver = new ResizeObserver(updateHeight);
    containerObserver.observe(observedRef.current);

    const headerObservers = headerRefs
      .map(ref => {
        if (ref.current) {
          const obs = new ResizeObserver(updateHeight);
          obs.observe(ref.current);
          return obs;
        }
        return null;
      })
      .filter(Boolean);

    updateHeight();

    return () => {
      containerObserver.disconnect();
      headerObservers.forEach(obs => obs.disconnect());
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [observedRef, headerRefs, isActive, disableUpdate, minReportedHeight, magicOffset, roundingMode, updateHeight]);

  return remainingHeight;
};

export { ROUNDING_MODES, applyRounding, calculateRemainingHeight };

export default useRemainingHeight;
